<template>
  <div>
    <div class="bg-white p-4 rounded-md">
      <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
        {{ $t("Prices") }}
        <span class="text-sm text-gray-500">(TTC)</span>
      </p>

      <p class="text-red-500" v-if="!productIsInsurable">
        {{ $t("There are no contracts available at this price.") }}
      </p>

      <div v-else-if="contracts.length > 0 || monthlyPrice">
        <div v-if="contracts.length > 0" class="space-y-2">
          <div
            v-for="(contract, idx) in sortedContractsByTermLength"
            :key="idx"
            :class="[
              'rounded-lg p-1 transition-colors',
              isRecommendedContract(contract) ? 'ring-2 ring-primary border' : 'border border-transparent',
            ]"
          >
            <div class="flex justify-between items-start">
              <div v-if="contract.monthly" class="font-medium text-sm">
                <div v-if="hasMultipleContractsWithSamePriceBandMin(contract)">
                  <div class="flex items-center gap-2 flex-wrap">
                    <p class="font-bold" :class="getMpcpOfferColor(contract.offerMarketingTitle)">
                      {{ contract.offerMarketingTitle }}
                    </p>
                    <span
                      v-if="isRecommendedContract(contract)"
                      class="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-primary text-button-font-primary"
                    >
                      Recommandé
                    </span>
                  </div>
                  <span
                    v-if="getMpcpMaxCoverage(contract.offerMarketingTitle) !== null"
                    class="text-gray-500 text-sm italic"
                  >
                    Jusqu'à {{ formatPrice(getMpcpMaxCoverage(contract.offerMarketingTitle)) }}
                  </span>
                </div>
                <div v-else>
                  <span>{{ $t("Monthly") }}</span>
                </div>
              </div>
              <div class="text-right">
                <p>{{ formatPrice(contract.price) }} / mois</p>
                <p class="text-gray-500 text-sm italic">{{ formatPrice(contract.price * 12) }} par an</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="text-gray-500 text-sm" v-if="!productCategoryValue">
        {{ $t("Select an insurance plan.") }}
      </p>

      <p class="text-gray-500 text-sm" v-if="productRequired && productCategoryValue && !isPresent(price)">
        {{ $t("Enter the price of the product to be insured.") }}
      </p>

      <div class="flex" v-if="availableOfferedMonths">
        <p class="text-primary-dark text-sm pt-2 flex">
          Vos clients bénéficient de {{ offeredMonths }} mois offerts par Estaly 🎁
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SaleService from "@/api/services/sale";
import { formatPrice, getMpcpOfferColor, getMpcpMaxCoverage, isPresent } from "@estaly/ui";
import { toCamelCase } from "@/utils/utils";
import { getRecommendedOffer } from "@/components/pos/subscription/utils/recommendedOffer";

export default {
  emits: ["recommendedOffer", "update:productIsInsurable"],
  props: {
    price: String,
    productCategoryValue: String,
    offeredMonths: Number,
    contractType: String,
    productless: { type: Boolean, default: false },
    virtualProduct: { type: Boolean, default: false },
    advisoryAnswer: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => {
    return {
      contracts: [],
      monthlyPrice: null,
      productIsInsurable: true,
    };
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),
    sortedContractsByTermLength() {
      const contracts = this.contracts;
      const monthlyContracts = contracts.filter((contract) => contract.monthly);
      const annualContracts = contracts.filter((contract) => !contract.monthly);

      monthlyContracts.sort((a, b) => a.termLength - b.termLength);
      annualContracts.sort((a, b) => a.termLength - b.termLength);

      return [...annualContracts, ...monthlyContracts];
    },
    availableOfferedMonths() {
      return isPresent(this.offeredMonths);
    },
    productRequired() {
      return !this.productless && !this.virtualProduct;
    },
  },

  watch: {
    price() {
      if (this.productRequired && isPresent(this.productCategoryValue) && isPresent(this.price)) {
        this.getContracts();
      } else {
        this.resetAttributes();
      }
    },
    productCategoryValue: {
      handler(newValue) {
        this.resetAttributes();
        if (newValue && (this.productless || this.virtualProduct || isPresent(this.price))) {
          this.getContracts();
        }
      },
      immediate: true,
    },
    virtualProduct(isVirtual) {
      if (isVirtual && isPresent(this.productCategoryValue)) {
        this.getContracts();
      } else {
        this.resetAttributes();
      }
    },
    contracts: {
      handler() {
        this.emitRecommendedOffer();
      },
    },
    advisoryAnswer: {
      handler() {
        this.emitRecommendedOffer();
      },
      deep: true,
      immediate: true,
    },
    productIsInsurable: {
      handler(val) {
        this.$emit("update:productIsInsurable", val);
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    isPresent,
    formatPrice,
    getMpcpOfferColor,
    getMpcpMaxCoverage,

    resetAttributes() {
      this.contracts = [];
      this.monthlyPrice = null;
      this.productIsInsurable = true;
    },

    hasMultipleContractsWithSamePriceBandMin(contract) {
      if (!contract.priceBandMin) return false;
      return this.contracts.filter((c) => c.priceBandMin === contract.priceBandMin).length > 1;
    },

    emitRecommendedOffer() {
      const recommended = getRecommendedOffer(this.contracts, {
        advisoryAnswer: this.advisoryAnswer,
        productPrice: this.price,
      });
      const payload = recommended ? { id: recommended.id, title: recommended.offerMarketingTitle } : null;
      this.$emit("recommendedOffer", payload);
    },

    isRecommendedContract(contract) {
      const id = this.advisoryAnswer?.recommendedOfferId;
      if (id != null && contract.id === id) return true;
      return false;
    },

    async getContracts() {
      const response = await SaleService.seeContractDetails(
        isPresent(this.productCategoryValue) ? this.productCategoryValue : "",
        this.price,
        this.selectedStoreId,
      );

      if (!response?.success) return;

      const formattedResponse = toCamelCase(response.data);
      this.contracts = formattedResponse.contracts;
      this.monthlyPrice = formattedResponse.monthlyPriceEquivalent;
      this.productIsInsurable = formattedResponse.productIsInsurable;
      this.emitRecommendedOffer();
    },
  },
};
</script>
